<template>
    <div
        class="border-b border-divider"
        data-spec-class="base-tabs"
    >
        <nav
            :class="classObject"
            data-spec-id="tabs"
        >
            <BaseTabsItem
                v-for="tab in tabItems"
                :id="tab.id"
                :key="tab.id"
                :label="tab.label"
                :badge="tab.badge"
                :is-active="tab.id === activeTabId"
                :theme="theme"
                :data-spec-id="`tab-${tab.id}`"
                @activate="onActivate"
            />
            <input
                name="tab"
                type="hidden"
                :value="activeTabId"
                aria-hidden="true"
            />
        </nav>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, defineEmits, defineProps, computed, withDefaults, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getUrlParam } from '@/utils/windowUtils';
import { TabsItem, TabsItemTheme } from '@/types';

const router = useRouter();

interface Props {
    tabItems: TabsItem[];
    urlParameter?: string;
    theme?: TabsItemTheme;
}

const props = withDefaults(defineProps<Props>(), {
    urlParameter: 'tab',
    theme: TabsItemTheme.Normal,
});

const emits = defineEmits(['change']);

const tabIds = computed(() => props.tabItems.map((tabItem: TabsItem) => tabItem.id));

const activeTabId = ref(tabIds.value[0]);

onMounted(() => {
    const urlTabParam = getUrlParam(props.urlParameter);
    if (urlTabParam && tabIds.value.includes(urlTabParam)) {
        activeTabId.value = urlTabParam;
    }

    emits('change', activeTabId.value);
});

watch(activeTabId, async (newActiveTabId) => {
    emits('change', newActiveTabId);
});

const onActivate = (tabId) => {
    activeTabId.value = tabId;
    router.replace({ query: { tab: tabId } });
};

const normalClassObject = computed(() => ({
    'flex gap-x-6 text-base py-3 font-semibold text-secondary hover:text-primary': true,
}));

const cardClassObject = computed(() => ({
    '-mb-px flex space-x-1': true,
}));

const classObject = computed(() => {
    switch (props.theme) {
        case TabsItemTheme.Normal:
            return normalClassObject.value;
        case TabsItemTheme.Card:
            return cardClassObject.value;
        default:
            return normalClassObject.value;
    }
});
</script>
